/**
 * The switch that lets a ringing phone reach outside the browser.
 *
 * In the rail's Settings section beside the audio panel, because it answers the
 * same kind of question - what this MACHINE does when the phone rings - rather
 * than anything about the account. The permission it asks for is granted per
 * browser profile, so the account-wide Settings page would have been the wrong
 * home twice over: the answer would not travel with the setting, and the switch
 * would be three menus away from the operator who has just missed a call.
 *
 * Drawn only where there is something to switch. A browser with no Notification
 * API gets no row at all: an inert control that explains why it is inert is
 * worse than the space it takes.
 *
 * ⚠ The permission is asked for from a CLICK, and it has to stay that way.
 * Browsers ignore - and Chrome permanently blocks - a permission prompt raised
 * without a gesture, and a prompt raised while the phone is ringing would land
 * on top of the answer button.
 */
import { useCallback, useState } from "react";
import { BellRing } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import {
  readAlertEnabled,
  toggleStep,
  writeAlertEnabled,
} from "../../../telephony/incoming/callAlert";
import { alertPermission } from "../../../telephony/incoming/useCallAlert";

export function RailAlert() {
  const t = useT();
  const [permission, setPermission] = useState(alertPermission);
  const [enabled, setEnabled] = useState(readAlertEnabled);

  const toggle = useCallback(() => {
    const step = toggleStep(!enabled, permission);
    if (step === "unsupported" || step === "blocked") {
      // Re-read rather than ignore: the operator may have unblocked the site in
      // the browser's own settings since this component mounted, and then the
      // next press should work rather than repeat the refusal.
      setPermission(alertPermission());
      return;
    }
    if (step === "disable") {
      setEnabled(false);
      writeAlertEnabled(false);
      return;
    }
    if (step === "enable") {
      setEnabled(true);
      writeAlertEnabled(true);
      return;
    }
    void Notification.requestPermission()
      .then((perm) => {
        const next = perm === "granted";
        setPermission(next ? "granted" : perm === "denied" ? "denied" : "default");
        setEnabled(next);
        writeAlertEnabled(next);
      })
      .catch(() => {
        // An older browser whose requestPermission takes a callback instead of
        // returning a promise. Nothing is switched on, and the row says so.
        setPermission(alertPermission());
      });
  }, [enabled, permission]);

  if (permission === "unsupported") return null;

  const blocked = permission === "denied";
  const on = enabled && permission === "granted";

  return (
    <>
      <div className="crm-msect">{t("calls.alert.section")}</div>
      <button
        type="button"
        className={cn("crm-mtag", on && "is-on")}
        aria-pressed={on}
        title={
          blocked
            ? t("calls.alert.blockedHint")
            : on
              ? t("calls.alert.onHint")
              : t("calls.alert.offHint")
        }
        onClick={toggle}
      >
        <i
          style={{
            background: blocked
              ? "var(--color-warning)"
              : on
                ? "var(--color-success)"
                : "var(--color-muted-foreground)",
          }}
        />
        <BellRing className="size-3.5" strokeWidth={2} />
        <span className="crm-mtag-l">
          {blocked ? t("calls.alert.blocked") : on ? t("calls.alert.on") : t("calls.alert.off")}
        </span>
      </button>
    </>
  );
}
